import { S3Client, S3ClientConfig } from '@aws-sdk/client-s3';
import { Progress, Upload } from '@aws-sdk/lib-storage';

import { fetchToken } from '@/services';
import { TIMEOUT } from '@/utils/const';
import { getSafeFileName } from '@/utils/fileUtil';
import {
  ErrorType,
  IRequestTokenData,
  IUploaderProps,
  IUploadInstanceOptions,
} from '@/utils/Uploader/type';

import { UploaderBase } from './base';

export class AwsUploader implements UploaderBase {
  TIMEOUT: number = TIMEOUT;
  uploadId = '';
  public client: S3Client | null = null;
  private uploader: Upload | null = null;
  private options: IUploaderProps | null = null;

  constructor(private callbackOptions: IUploadInstanceOptions) {}

  // 上传
  public async upload(options: IUploaderProps): Promise<string | null> {
    return new Promise(async (resolve) => {
      const { onError, onBefore, onProgress, onSuccess, onStart, onCancel } =
        this.callbackOptions;
      const { tokenData, file, fileUid } = options;
      const fileId = fileUid || '';

      const {
        region,
        accessKey,
        accessSecret,
        bucket,
        accessUrl,
        filePath,
        securityToken,
      } = tokenData;

      this.options = options;

      // 获取client
      const client = this.getClient({
        region,
        credentials: {
          accessKeyId: accessKey,
          secretAccessKey: accessSecret,
          sessionToken: securityToken,
        },
        // 尝试次数
        maxAttempts: 10,
        retryStrategy: {
          retry: (next, args) => {
            return next(args);
          },
        },
      });
      this.client = client;
      const start = new Date().getTime();
      try {
        if (!client) {
          const err = new Error('Get AWS Client Error');
          err.name = ErrorType.Other;
          throw err;
        }

        // 创建一个Upload实例
        const uploader = new Upload({
          client,
          params: {
            Bucket: bucket,
            Key: filePath,
            Body: file,
          },
        });
        this.uploader = uploader;
        if (!this.uploader) {
          const err = new Error('Get AWS Uploader Error');
          err.name = ErrorType.Other;
          throw err;
        }

        // 进度
        uploader.on('httpUploadProgress', async (progress: Progress) => {
          const { loaded, total } = progress;
          if (loaded && total) {
            onProgress &&
              onProgress({
                progress: loaded / total,
                status: loaded / total >= 1,
                url: accessUrl,
              });
          }
        });

        // 开始上传前的回调
        try {
          onBefore && (await onBefore({ file }));
        } catch (error) {
          console.error(error);
          resolve(null);
          return;
        }

        onStart && onStart(start);
        // 开始上传
        const res = await uploader.done();

        if (res && res.$metadata) {
          const code = res.$metadata.httpStatusCode;
          if (code && String(code).startsWith('2')) {
            onSuccess &&
              (await onSuccess(accessUrl, {
                start,
                end: new Date().getTime(),
              }));
            resolve(accessUrl);
            return;
          }
        }
        resolve(null);
      } catch (error: any) {
        console.log(error, '---error');
        if (error.name === 'AbortError') {
          onCancel && (await onCancel(fileId));
          return;
        }
        const err = new Error(error?.message || '');
        err.name = error?.name || ErrorType.Other;
        onError &&
          onError(
            {
              name: error.name || ErrorType.Other,
              message: error.message || '',
              start,
              end: new Date().getTime(),
              error: err,
            },
            fileId,
            this,
          );
        resolve(null);
      }
    });
  }

  public async cancel() {
    if (this.uploader) {
      const res = await this.uploader.abort();
      // const fileId = (this.options && this.options.fileUid) || '';
      // const { onCancel } = this.callbackOptions;
      // onCancel && (await onCancel(fileId));
      return res;
    }
    return null;
  }

  public async retry() {
    // todo 从断点处重新上传
    if (this.options) {
      return await this.upload(this.options);
    }
    return null;
  }

  private getClient(params: S3ClientConfig) {
    return new S3Client(params);
  }

  // 拿到oss的ak&sk&bucket等信息
  private async getAuthData(params: IRequestTokenData) {
    try {
      const { fileName, dirSceneType } = params;
      const name = getSafeFileName(fileName);
      const data = await fetchToken(name, dirSceneType);
      if (data.code !== 200 || !data.data) return null;
      return data.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
